import { FC, Fragment, useCallback } from "react";
import { Stack, Typography } from "@mui/material";
import { BillingExplorerChartSeriesSelect } from "@/components/pages/resource-explorer/components/settings-panel/components/chart-settings-section/components/BillingExplorerChartSeriesSelect";
import { BillingExplorerChartScaleSelect } from "@/components/pages/resource-explorer/components/settings-panel/components/chart-settings-section/components/BillingExplorerChartScaleSelect";
import { BillingExplorerChartSettingSwitcher } from "@/components/pages/resource-explorer/components/settings-panel/components/chart-settings-section/components/BillingExplorerChartSettingSwitcher";
import { BillingExplorerShowBudgetLineSettingSwitcher } from "@/components/pages/resource-explorer/components/settings-panel/components/chart-settings-section/components/BillingExplorerShowBudgetLineSettingSwitcher";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { resourceExplorerChartSettingsSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/chart/settings/resourceExplorerChartSettingsSelector";
import { resourceExplorerDataPointSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/data/resourceExplorerDataPointSelector";
import { billingExplorerChartTypeSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/billingExplorerChartTypeSelector";
import { resourceExplorerPathLastItemFilterTreeSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/path/resourceExplorerPathLastItemFilterTreeSelector";
import { setResourceExplorerDataPoint } from "@/store/resource-explorer/resourceExplorerSlice";
import { getResourceExplorerDataThunk } from "@/store/resource-explorer/thunks/widgets/getResourceExplorerDataThunk";
import ChartSettingsSvgIcon from "@/assets/icons/chart/ChartSettingsSvgIcon";

export const BillingExplorerChartSettingsSection: FC = () => {
  const dispatch = useAppDispatch();
  const settings = useAppSelector(resourceExplorerChartSettingsSelector);
  const dataPoint = useAppSelector(resourceExplorerDataPointSelector);
  const chartType = useAppSelector(billingExplorerChartTypeSelector);

  const pathLastItemFilterTree = useAppSelector(
    resourceExplorerPathLastItemFilterTreeSelector,
  );

  const displayForecastChangeHandler = useCallback(() => {
    if (dataPoint !== "daily") {
      dispatch(setResourceExplorerDataPoint("daily"));
      dispatch(getResourceExplorerDataThunk(pathLastItemFilterTree));
    }
  }, [dispatch, dataPoint, pathLastItemFilterTree]);

  if (!settings) {
    return;
  }

  return (
    <Stack px={1.5}>
      <Stack direction="row" spacing={1} pb={1.5}>
        <ChartSettingsSvgIcon />

        <Typography variant="body1" fontWeight="medium">
          Chart Settings
        </Typography>
      </Stack>

      <Stack spacing={2} direction="row" alignItems="center">
        <BillingExplorerChartSeriesSelect series={settings.series ?? 10} />

        <BillingExplorerChartScaleSelect scale={settings.scale ?? "linear"} />
      </Stack>

      <BillingExplorerChartSettingSwitcher
        type="show_data_labels"
        checked={!!settings.show_data_labels}
      />
      <BillingExplorerChartSettingSwitcher
        type="show_legends"
        checked={!!settings.show_legends}
      />

      {chartType !== "pie" && (
        <Fragment>
          <BillingExplorerChartSettingSwitcher
            type="cumulative_line"
            checked={!!settings.cumulative_line}
          />
          <BillingExplorerChartSettingSwitcher
            type="display_forecast"
            onChange={displayForecastChangeHandler}
            checked={!!settings.display_forecast}
          />
          <BillingExplorerShowBudgetLineSettingSwitcher />
        </Fragment>
      )}
    </Stack>
  );
};
